import { getParent, types, type Instance } from 'mobx-state-tree';
import type { IInspectorParent } from './inspector_parent';

/** The change keys stored by the dependency inspector. */
type DependencyChangeKey = 'masterName' | 'dependentName' | 'type';
type DependencyType = 'OPTIONAL_N' | 'OPTIONAL_1' | 'MANDATORY_N' | 'MANDATORY_1';
/** A change value is a name string or a dependency type, depending on the key. */
type DependencyChangeValue = string | DependencyType | null;

/**
 * Store the changes in the dependency inspector that are currently pending.
 */
const DependencyInspectorChanges = types
  .model('DependencyInspectorChanges', {
    masterName: types.maybeNull(types.string),
    dependentName: types.maybeNull(types.string),
    type: types.maybeNull(
      types.enumeration(['OPTIONAL_N', 'OPTIONAL_1', 'MANDATORY_N', 'MANDATORY_1'])
    ),
  })
  .views((self) => ({
    // the inspector is dirty if any of the attributes has changes
    get dirty() {
      return self.masterName !== null || self.dependentName !== null || self.type !== null;
    },
  }))
  .actions((self) => ({
    /**
     * Store a change for the given key.
     *
     * This action also receives the original value of the attribute and clears
     * the store change if the new value for the change is the same as the
     * original value.
     */
    storeChange(
      key: DependencyChangeKey,
      original: DependencyChangeValue,
      change: DependencyChangeValue
    ) {
      // Dynamic indexed write: TS computes the write-target of `self[key]` as the
      // intersection of every prop type, which no single value satisfies. View
      // the change props through a uniform record for the assignment — MST
      // narrows at runtime, so this is type-only and byte-identical.
      const writable = self as unknown as Record<DependencyChangeKey, DependencyChangeValue>;
      if (original !== change) {
        writable[key] = change;
      } else {
        writable[key] = null;
      }
    },
    discard() {
      self.masterName = null;
      self.dependentName = null;
      self.type = null;
    },
  }));

/**
 * UI state for the object inspector
 */
const DependencyInspector = types
  .model('DependencyInspector', {
    // our changes
    changes: types.optional(DependencyInspectorChanges, () =>
      DependencyInspectorChanges.create({
        masterName: null,
        dependentName: null,
        type: null,
      })
    ),
  })
  .views((self) => ({
    get dirty() {
      return self.changes.dirty;
    },
  }))
  .actions((self) => ({
    discardChanges() {
      self.changes.discard();
    },
    setProjectBusy(busy: boolean) {
      getParent<IInspectorParent>(self).setProjectBusy(busy);
    },
  }));

export type IDependencyInspector = Instance<typeof DependencyInspector>;

export default DependencyInspector;
